import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import RecipePriceDetails from '../components/RecipePriceDetails';
import SignInPopup from '../components/SignInPopup';
import { useGetUserID } from '../hooks/useGetUserID';
import { useSignInPopup } from '../hooks/useSignInPopup';
import ServingSizeSelector from '../components/ServingSizeSelector';

export default function OtherRecipeDetails() {
    const { recipeId } = useParams();
    const navigate = useNavigate();
    const userID = useGetUserID();
    const getAuthToken = () => window.localStorage.getItem('auth_token');
    const isAuthenticated = !!(userID && getAuthToken());
    const { showPopup: showSignInPopup, handleClosePopup: handleCloseSignInPopup, openPopup: openSignInPopup } = useSignInPopup(isAuthenticated, 'otherrecipedetails');
    const [recipe, setRecipe] = useState(null);
    const [savedRecipes, setSavedRecipes] = useState([]);
    const [servingSizes, setServingSizes] = useState({});
    const [recipeCosts, setRecipeCosts] = useState({});
    const [ingredientPrices, setIngredientPrices] = useState({});

    useEffect(() => {
        const fetchRecipe = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/recipes/${recipeId}`);
                setRecipe(response.data);
            } catch (error) {
                console.error("Error fetching recipe:", error);
                alert("Error fetching recipe details");
                navigate("/searchrecipes");
            }
        };

        const fetchSavedRecipes = async () => {
            try {
                if (!userID) return;
                const response = await axios.get(`${API_BASE_URL}/recipes/savedRecipes/ids/${userID}`);
                setSavedRecipes(response.data.savedRecipes);
            } catch (error) {
                console.error("Error fetching saved recipes:", error);
            }
        };

        fetchRecipe();
        if (userID) fetchSavedRecipes();
    }, [recipeId, userID, navigate]);

    const isRecipeSaved = (recipeId) => {
        return savedRecipes?.some(id => id.toString() === recipeId.toString());
    };

    const saveRecipe = async () => {
        try {
            const response = await axios.put(`${API_BASE_URL}/recipes`, 
                { recipeID: recipeId, userID }
            );
            setSavedRecipes([...savedRecipes, recipeId]);
            alert(response.data.message || "Recipe saved successfully!");
        } catch (error) {
            alert(error.response?.data?.message || "Error saving recipe");
        }
    };

    const unsaveRecipe = async () => {
        try {
            const response = await axios.put(`${API_BASE_URL}/recipes/unsave`, 
                { recipeID: recipeId, userID }
            );
            setSavedRecipes(savedRecipes.filter(id => id !== recipeId));
            alert("Recipe unsaved successfully!");
        } catch (error) {
            alert(error.response?.data?.message || "Error unsaving recipe");
        }
    };

    const handleServingSizeConfirm = (recipeId, newServings) => {
        setServingSizes(prev => ({
            ...prev,
            [recipeId]: newServings
        }));
        setRecipeCosts(prev => ({
            ...prev,
            [recipeId]: null
        }));
    };

    const fetchRecipeCost = async () => {
        try {
            const currentServings = servingSizes[recipeId] || recipe.servings;
            const scaledIngredients = recipe.ingredients.map(ing => ({
                ...ing,
                amount: (ing.amount * currentServings) / recipe.servings
            }));

            const response = await axios.post(`${API_BASE_URL}/recipes/${recipeId}/calculate-cost`, {
                ingredients: scaledIngredients
            });
            
            setRecipeCosts(prev => ({ ...prev, [recipeId]: response.data.totalCost }));
            setIngredientPrices(response.data.ingredientPrices);
        } catch (error) {
            console.error("Error fetching recipe cost:", error);
            alert("Error calculating recipe cost");
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API_BASE_URL}/recipes/${recipeId}`);
            alert("Recipe deleted successfully!");
            navigate("/");
        } catch (error) {
            // Error handling...
        }
    };

    if (!recipe) return <div>Loading...</div>;

    return (
        <div className="recipe-details" style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1>{recipe.name}</h1>
                <div className="recipe-actions" style={{ display: 'flex', gap: '10px' }}>
                    <button 
                        onClick={isRecipeSaved(recipeId) ? unsaveRecipe : saveRecipe}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        {isRecipeSaved(recipeId) ? 'Unsave Recipe' : 'Save Recipe'}
                    </button>
                </div>
            </div>

            <img 
                src={recipe.imageUrl} 
                alt={recipe.name}
                style={{ 
                    width: '100%',
                    height: '400px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    marginBottom: '20px'
                }}
            />

            <ServingSizeSelector
                baseServings={recipe.servings}
                currentServings={servingSizes[recipeId] || recipe.servings}
                onServingSizeConfirm={(newServings) => handleServingSizeConfirm(recipeId, newServings)}
            />

            <div className="recipe-content">
                <h3>Ingredients:</h3>
                <ul style={ { listStyle: 'none', padding: 0 }}>
                    {recipe.ingredients.map((ingredient, index) => (
                        <li key={index} className="recipe-ingredients">
                            { (ingredient.amount * (servingSizes[recipeId] || recipe.servings)) / recipe.servings} 
                            {ingredient.unit === 'other' ? ingredient.customUnit : ingredient.unit} 
                            {` ${ingredient.name}`}
                        </li>
                    ))}
                </ul>

                <h3>Instructions:</h3>
                <ol style={{ paddingLeft: '20px' }}>
                    {recipe.instructions.map((step, index) => (
                        <li key={index} className="recipe-steps">
                            {step}
                        </li>
                    ))}
                </ol>

                <button 
                    onClick={fetchRecipeCost}
                    style={{
                        width: '100%',
                        padding: '8px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginTop: '20px'
                    }}
                >
                    Calculate Cost
                </button>

                {recipeCosts[recipeId] && (
                    <RecipePriceDetails 
                        ingredients={recipe.ingredients.map(ing => ({
                            ...ing,
                            amount: (ing.amount * (servingSizes[recipeId] || recipe.servings)) / recipe.servings
                        }))} 
                        ingredientPrices={ingredientPrices}
                        onRequireSignIn={openSignInPopup}
                    />
                )}

                <SignInPopup show={showSignInPopup} onClose={handleCloseSignInPopup} />
            </div>
        </div>
    );
}
